import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const effortData = [
  { metric: 'Power', value: 78 },
  { metric: 'Endurance', value: 65 },
  { metric: 'Speed', value: 82 },
  { metric: 'Recovery', value: 71 },
  { metric: 'Strength', value: 59 },
  { metric: 'Flexibility', value: 45 },
];

const WorkoutIntensityPanel = ({ device = 'Polar' }) => {
  const [sessionActive, setSessionActive] = useState(true);
  const [elapsed, setElapsed] = useState(2847); // seconds
  const [power, setPower] = useState(245);
  const [cadence, setCadence] = useState(88);

  useEffect(() => {
    if (!sessionActive) return;
    const interval = setInterval(() => {
      setElapsed(prev => prev + 1);
      setPower(prev => Math.max(180, Math.min(320, prev + Math.floor(Math.random() * 11) - 5)));
      setCadence(prev => Math.max(75, Math.min(105, prev + Math.floor(Math.random() * 5) - 2)));
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionActive]);

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0
      ? `${h}:${String(m)?.padStart(2, '0')}:${String(s)?.padStart(2, '0')}`
      : `${String(m)?.padStart(2, '0')}:${String(s)?.padStart(2, '0')}`;
  };

  const intensityLevel = power > 280 ? 'High' : power > 220 ? 'Moderate' : 'Low';
  const intensityColor = power > 280 ? '#ef4444' : power > 220 ? '#f97316' : '#22c55e';

  const stats = [
    { label: 'Power', value: `${power}W`, icon: 'Zap', color: '#f59e0b' },
    { label: 'Cadence', value: `${cadence} rpm`, icon: 'RotateCw', color: '#06b6d4' },
    { label: 'Pace', value: '5:42/km', icon: 'Timer', color: '#8b5cf6' },
    { label: 'VO2 Est.', value: '48.2', icon: 'Wind', color: '#22c55e' },
  ];

  return (
    <div className="bg-card rounded-2xl p-5 border border-border/40 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-orange-500/10 flex items-center justify-center">
            <Icon name="Dumbbell" size={18} color="#f97316" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Workout Intensity</h3>
            <p className="text-xs text-muted-foreground">via {device}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <motion.div
            animate={{ opacity: sessionActive ? [1, 0.4, 1] : 1 }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium"
            style={{ backgroundColor: `${intensityColor}15`, color: intensityColor }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: intensityColor }} />
            {sessionActive ? 'Active' : 'Paused'}
          </motion.div>
        </div>
      </div>
      {/* Session timer + intensity */}
      <div className="flex items-center gap-4 mb-4 bg-muted/20 rounded-xl p-3">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground font-mono">{formatTime(elapsed)}</div>
          <div className="text-xs text-muted-foreground">Duration</div>
        </div>
        <div className="w-px h-10 bg-border/60" />
        <div className="text-center">
          <div className="text-xl font-bold" style={{ color: intensityColor }}>{intensityLevel}</div>
          <div className="text-xs text-muted-foreground">Intensity</div>
        </div>
        <div className="w-px h-10 bg-border/60" />
        <div className="text-center flex-1">
          <div className="text-xl font-bold text-foreground">387</div>
          <div className="text-xs text-muted-foreground">kcal burned</div>
        </div>
      </div>
      {/* Live stats grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {stats?.map((s) => (
          <div key={s?.label} className="bg-muted/20 rounded-xl p-2.5 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${s?.color}15` }}>
              <Icon name={s?.icon} size={13} color={s?.color} />
            </div>
            <div>
              <div className="text-sm font-bold text-foreground leading-none">{s?.value}</div>
              <div className="text-xs text-muted-foreground">{s?.label}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Radar chart */}
      <div className="flex-1 min-h-0">
        <p className="text-xs text-muted-foreground mb-1 font-medium">Performance Profile</p>
        <ResponsiveContainer width="100%" height={130}>
          <RadarChart data={effortData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
            <PolarGrid stroke="#e2e8f0" />
            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#94a3b8' }} />
            <Tooltip formatter={(v) => [`${v}%`, 'Score']} />
            <Radar dataKey="value" stroke="#f97316" fill="#f97316" fillOpacity={0.25} strokeWidth={2} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WorkoutIntensityPanel;
